import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';

function autoWrapCommonLatex(input) {
  if (!input || typeof input !== 'string') return '';

  let output = input;

  // Only wrap bare LaTeX commands that aren't already inside $ delimiters
  // Split by $ to avoid wrapping things already wrapped
  const parts = output.split(/(\$[^$]*\$)/);
  output = parts.map((part, idx) => {
    // Even indices are outside $ delimiters; odd indices are inside
    if (idx % 2 === 1) return part; // Already wrapped, skip
    
    // Wrap bare \frac{a}{b}
    let p = part.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$\\frac{$1}{$2}$');
    // Wrap bare \sqrt{x}
    p = p.replace(/\\sqrt\{([^}]+)\}/g, '$\\sqrt{$1}$');
    // Wrap bare x^{n}
    p = p.replace(/([a-zA-Z0-9])\^\{([^}]+)\}/g, '$$$1^{$2}$$');
    // Wrap bare x_{n}
    p = p.replace(/([a-zA-Z0-9])_\{([^}]+)\}/g, '$$$1_{$2}$$');
    
    return p;
  }).join('');

  return output;
}

function splitMathAndText(text) {
  const pattern = /(\$\$[\s\S]+?\$\$|\$[^$]+\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\])/g;
  return text.split(pattern).filter(Boolean);
}

function isBlockMathToken(token) {
  return token.startsWith('$$') || token.startsWith('\\[');
}

function stripDelimiters(token) {
  if (token.startsWith('$$') && token.endsWith('$$')) return token.slice(2, -2);
  if (token.startsWith('$') && token.endsWith('$')) return token.slice(1, -1);
  if (token.startsWith('\\(') && token.endsWith('\\)')) return token.slice(2, -2);
  if (token.startsWith('\\[') && token.endsWith('\\]')) return token.slice(2, -2);
  return token;
}

export default function MathText({ text, className = '' }) {
  const safeText = autoWrapCommonLatex(text || '');
  const parts = splitMathAndText(safeText);

  return (
    <span className={className}>
      {parts.map((part, idx) => {
        const isMath =
          (part.startsWith('$$') && part.endsWith('$$')) ||
          (part.startsWith('$') && part.endsWith('$')) ||
          (part.startsWith('\\(') && part.endsWith('\\)')) ||
          (part.startsWith('\\[') && part.endsWith('\\]'));

        if (!isMath) {
          return <span key={`text-${idx}`} className="whitespace-pre-wrap">{part}</span>;
        }

        const expr = stripDelimiters(part);
        if (isBlockMathToken(part)) {
          return (
            <span key={`math-block-${idx}`} className="block my-2 overflow-x-auto">
              <BlockMath math={expr} />
            </span>
          );
        }

        return <InlineMath key={`math-inline-${idx}`} math={expr} />;
      })}
    </span>
  );
}
